/**
 * ChatbotUsageReport.js — per-customer / per-chatbot usage report.
 *
 * Daily (date), monthly (month) or date-range reports with an optional per-day /
 * per-month series. Backed by GET /api/chatbot/reports/usage (also the external API
 * for application tokens); CSV via ?format=csv.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Download, ChevronRight, ChevronDown, Code, AlertTriangle } from 'lucide-react';
import { apiCall, apiCallJson } from './Api';

const CATEGORIES = ['authentication', 'marketing', 'service', 'utility'];
const today = () => new Date().toISOString().slice(0, 10);
const fmtInt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-IN'));
const fmtMoney = (v, ccy) => (v === null || v === undefined ? '—'
  : `${ccy ? ccy + ' ' : ''}${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-xs font-semibold text-gray-600 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);
const Td = ({ children, right, className = '' }) => (
  <td className={`px-3 py-2 text-sm whitespace-nowrap ${right ? 'text-right tabular-nums' : ''} ${className}`}>{children}</td>
);

const MetricCells = ({ row, currency }) => {
  const m = row.messages, c = row.conversations, mc = row.messageCost;
  return (
    <>
      <Td right>{fmtInt(m.received)}</Td>
      <Td right>{fmtInt(m.sent)}</Td>
      <Td right>{fmtInt(m.delivered)}</Td>
      <Td right>{fmtInt(m.read)}</Td>
      <Td right>{fmtInt(m.failed)}</Td>
      <Td right>{fmtInt(c.started)}</Td>
      <Td right>{fmtInt(c.handedOff)}</Td>
      {CATEGORIES.map((cat) => (
        <Td right key={cat}>
          <div>{fmtInt(mc[cat]?.messages)}</div>
          <div className="text-[11px] text-gray-400">{fmtMoney(mc[cat]?.cost)}</div>
        </Td>
      ))}
      <Td right className="font-semibold">{fmtMoney(mc.totalCost, currency)}</Td>
    </>
  );
};

export default function ChatbotUsageReport() {
  const [mode, setMode] = useState('day'); // day | month | range
  const [day, setDay] = useState(today());
  const [month, setMonth] = useState(today().slice(0, 7));
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [groupBy, setGroupBy] = useState('none');
  const [customerId, setCustomerId] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [showApi, setShowApi] = useState(false);

  useEffect(() => {
    apiCallJson('/api/chatbot/workflows/bill-to-options')
      .then((d) => setCustomers(d.customers || [])).catch(() => setCustomers([]));
    apiCallJson('/api/chatbot/workflows?limit=200')
      .then((d) => setWorkflows(d.workflows || [])).catch(() => setWorkflows([]));
  }, []);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    if (mode === 'day') q.set('date', day);
    else if (mode === 'month') q.set('month', month);
    else { q.set('start_date', start); q.set('end_date', end); }
    if (groupBy !== 'none') q.set('group_by', groupBy);
    if (customerId) q.append('customer_id', customerId);
    if (workflowId) q.append('workflow_id', workflowId);
    return q;
  }, [mode, day, month, start, end, groupBy, customerId, workflowId]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setReport(await apiCallJson(`/api/chatbot/reports/usage?${query.toString()}`));
      setExpanded({});
    } catch (e) {
      setError(e.message || 'Failed to load report'); setReport(null);
    } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    const q = new URLSearchParams(query); q.set('format', 'csv');
    const resp = await apiCall(`/api/chatbot/reports/usage?${q.toString()}`);
    if (!resp.ok) { setError(`CSV export failed (${resp.status})`); return; }
    const blob = await resp.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const r = report?.report;
    a.download = `chatbot-usage-${r?.startDate || 'report'}_${r?.endDate || ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const r = report?.report;
  const apiPath = `${window.location.origin}/api/chatbot/reports/usage?${query.toString()}`;

  return (
    <div className="h-full flex flex-col">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 px-5 py-3 border-b bg-gray-50 shrink-0">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Period</label>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white">
            {[['day', 'Daily'], ['month', 'Monthly'], ['range', 'Date range']].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)}
                className={`px-3 py-1.5 text-sm ${mode === v ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}>{l}</button>
            ))}
          </div>
        </div>
        {mode === 'day' && (
          <div><label className="block text-xs text-gray-500 mb-1">Date</label>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" /></div>
        )}
        {mode === 'month' && (
          <div><label className="block text-xs text-gray-500 mb-1">Month</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" /></div>
        )}
        {mode === 'range' && (
          <>
            <div><label className="block text-xs text-gray-500 mb-1">From</label>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">To</label>
              <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" /></div>
          </>
        )}
        <div><label className="block text-xs text-gray-500 mb-1">Breakdown</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
            <option value="none">Totals only</option>
            <option value="day">By day</option>
            <option value="month">By month</option>
          </select></div>
        <div><label className="block text-xs text-gray-500 mb-1">Customer</label>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm max-w-[200px]">
            <option value="">All customers</option>
            {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select></div>
        <div><label className="block text-xs text-gray-500 mb-1">Chatbot</label>
          <select value={workflowId} onChange={(e) => setWorkflowId(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm max-w-[220px]">
            <option value="">All chatbots</option>
            {workflows.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select></div>
        <div className="flex-1" />
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
        <button onClick={downloadCsv} disabled={!r}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <Download className="h-4 w-4" />CSV</button>
        <button onClick={() => setShowApi(!showApi)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg ${showApi ? 'bg-gray-800 text-white border-gray-800' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
          <Code className="h-4 w-4" />API</button>
      </div>

      {showApi && (
        <div className="px-5 py-3 border-b bg-gray-900 text-gray-100 text-xs font-mono shrink-0 overflow-x-auto">
          <div className="text-gray-400 mb-1"># Same report for external systems (application token or user JWT). Add &amp;format=csv for CSV.</div>
          <div className="whitespace-pre">{`curl -H "Authorization: Bearer <token>" \\\n  "${apiPath}"`}</div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto">
        {error && <div className="m-5 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</div>}
        {!error && r && r.customers.length === 0 && (
          <div className="p-10 text-center text-sm text-gray-500">No chatbot activity for {r.startDate}{r.endDate !== r.startDate ? ` – ${r.endDate}` : ''}.</div>
        )}
        {!error && r && r.customers.length > 0 && (
          <table className="min-w-full">
            <thead className="bg-white sticky top-0 shadow-sm z-10">
              <tr className="border-b">
                <Th>Chatbot</Th><Th right>Received</Th><Th right>Sent</Th><Th right>Delivered</Th><Th right>Read</Th>
                <Th right>Failed</Th><Th right>Convs</Th><Th right>Handoffs</Th>
                {CATEGORIES.map((c) => <Th right key={c}>{c[0].toUpperCase() + c.slice(1)}</Th>)}
                <Th right>Total cost</Th>
              </tr>
            </thead>
            <tbody>
              {r.customers.map((cust) => (
                <React.Fragment key={cust.customerId}>
                  <tr className="bg-blue-50/60 border-b">
                    <Td className="font-semibold text-gray-800">{cust.customerName}
                      <span className="ml-2 text-xs font-normal text-gray-500">{cust.totals.bots} bot{cust.totals.bots === 1 ? '' : 's'}</span></Td>
                    <Td right>{fmtInt(cust.totals.received)}</Td><Td right>{fmtInt(cust.totals.sent)}</Td>
                    <Td right>{fmtInt(cust.totals.delivered)}</Td><Td right>{fmtInt(cust.totals.read)}</Td>
                    <Td right>{fmtInt(cust.totals.failed)}</Td><Td right>{fmtInt(cust.totals.conversations)}</Td>
                    <Td right>{fmtInt(cust.totals.handedOff)}</Td>
                    {CATEGORIES.map((c) => <Td key={c} />)}
                    <Td right className="font-semibold">{cust.currency ? fmtMoney(cust.totals.totalCost, cust.currency) : 'mixed currency'}</Td>
                  </tr>
                  {cust.bots.map((bot) => {
                    const open = !!expanded[bot.botId];
                    const hasSeries = (bot.series || []).length > 0;
                    return (
                      <React.Fragment key={bot.botId}>
                        <tr className="border-b hover:bg-gray-50">
                          <Td>
                            <button disabled={!hasSeries} onClick={() => setExpanded({ ...expanded, [bot.botId]: !open })}
                              className="inline-flex items-center gap-1 text-gray-800 disabled:cursor-default">
                              {hasSeries ? (open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
                              <span className="pl-2">{bot.botName}</span>
                            </button>
                            <div className="pl-7 text-[11px] text-gray-400">{bot.channels.join(', ') || 'no active channel'}</div>
                            {bot.notes && bot.notes.map((n) => (
                              <div key={n} className="pl-7 text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{n}</div>
                            ))}
                          </Td>
                          <MetricCells row={bot} currency={bot.currency} />
                        </tr>
                        {open && bot.series.map((s) => (
                          <tr key={s.period} className="border-b bg-gray-50/70 text-gray-600">
                            <Td className="pl-12 text-xs">{s.period}</Td>
                            <MetricCells row={s} currency={bot.currency} />
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
        {r && (
          <div className="px-5 py-3 text-[11px] text-gray-400">
            Day boundaries: {r.timezone === 'customer' ? "each customer's timezone" : r.timezone}. Delivered/Read/Failed and category counts come from
            WhatsApp status webhooks (— = channel not tracked). Costs use the CHATBOT rate card per category
            (route &lt;channel&gt;:&lt;category&gt;, else the whole-service price), slabs applied over this report's volume.
          </div>
        )}
      </div>
    </div>
  );
}
